import React, { useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { MERCHANT_ADMIN_PRODUCTS_GROUP_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { ProductGroupFormValues } from "../productGroupTypes"
import { createProductGroup, ProductGroupQuery } from "../productGroupApi"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { SuccessDisplay } from "../../../../../core/api/errorTypes"

import { useNavigate } from "react-router-dom"
import { useDateTime } from "../../../../localization/useDateTime"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { FetchPriceListsResponse, PriceListQuery, fetchPriceLists } from "../../priceList/priceListApi"
import { useSelectedPriceList } from "../../priceList/priceListSelectionHooks"
import { prepareProductGroup } from "../helpers/prepareProductGroup"
import { prepareInitialValues } from "../helpers/prepareInitialValues"
import { CreateUpdateProductGroupForm } from "../components/CreateUpdateProductGroupForm"
import { FormikProps } from "formik"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"

const selectPriceLists = (data: FetchPriceListsResponse) => data?.priceLists || []

export function CreateProductGroupPage() {
    const formRef = useRef<FormikProps<ProductGroupFormValues>>(null)
    const { merchantId } = useMerchant()
    const navigate = useNavigate()
    const dateTimeFactory = useDateTime()
    const [selectedPriceListId] = useSelectedPriceList("productGroups")

    const { data: priceLists = [] } = useApiQuery(fetchPriceLists, {
        queryName: PriceListQuery.LIST,
        dependencies: { merchantId },
        select: selectPriceLists,
    })

    const defaultPriceList =
        typeof selectedPriceListId === "number"
            ? (priceLists.find((priceList) => priceList.id === selectedPriceListId) ?? null)
            : null

    const { mutate: callCreateProductGroup } = useApiMutation(createProductGroup, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ProductGroupQuery.LIST] })
            navigate(MERCHANT_ADMIN_PRODUCTS_GROUP_ROUTE)
        },
    })

    async function handleCreateProductGroup(values: ProductGroupFormValues) {
        const productGroup = prepareProductGroup(values, dateTimeFactory)

        callCreateProductGroup({
            merchantId,
            productGroup,
        })
    }

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="product-group-page-title-create" attrs={{ title: true }}>
                <PageHeader
                    title="Create product group"
                    page={Page.MERCHANT_PRODUCT_GROUP}
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    rightElement={
                        <LocalizedStrict id="create-product-group-create-button">
                            <StandardButton onClick={handleSaveClick}>Create</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-product-groups">
                            <BreadcrumbLink to={MERCHANT_ADMIN_PRODUCTS_GROUP_ROUTE}>Product groups</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-product-group-create">
                            <Breadcrumb>Create product group</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <CreateUpdateProductGroupForm
                ref={formRef}
                initialValues={prepareInitialValues(dateTimeFactory, undefined, defaultPriceList)}
                onSubmit={handleCreateProductGroup}
            />
        </>
    )
}

